import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Copy, Check } from "lucide-react";

export default function TwoFactorCard() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try { const r = await api.get("/auth/2fa/status"); setStatus(r.data.enabled); }
    catch { setStatus(false); }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
    } catch { toast.error("Could not start 2FA setup"); }
    finally { setLoading(false); }
  };

  const enable = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("2FA enabled — your account is now protected");
      setSetup(null); setCode(""); load();
    } catch (e) { toast.error("Invalid code", { description: e?.response?.data?.detail }); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePw, code: disableCode });
      toast.success("2FA disabled");
      setDisablePw(""); setDisableCode(""); load();
    } catch (e) { toast.error("Could not disable", { description: e?.response?.data?.detail }); }
    finally { setLoading(false); }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setup?.secret || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="card-luxe p-6" data-testid="twofa-card">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck className="w-5 h-5 text-[#0B5D3B]" />
        <h3 className="font-serif text-xl">Security · Two-Factor Authentication</h3>
        {status && (
          <span className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-[#0B5D3B] bg-[#0B5D3B]/10 px-3 py-1 rounded-full">Enabled</span>
        )}
        {status === false && (
          <span className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-[#A88926] bg-[#D4AF37]/15 px-3 py-1 rounded-full">Off</span>
        )}
      </div>

      {status === null && <Loader2 className="animate-spin text-[#0B5D3B]" />}

      {status === false && !setup && (
        <div>
          <p className="text-sm text-[#4A554E] mb-4">Add an extra layer of security. After enabling, you'll need a 6-digit code from your authenticator app (Google Authenticator, Authy, 1Password, etc.) every time you sign in.</p>
          <button onClick={start} disabled={loading} className="btn-primary text-sm py-2.5 px-5" data-testid="twofa-start">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set up 2FA"}
          </button>
        </div>
      )}

      {setup && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="twofa-setup">
          <div>
            <p className="text-sm text-[#4A554E] mb-4"><strong>Step 1.</strong> Open your authenticator app and scan this QR code, or paste the secret below.</p>
            <img src={setup.qr_data_url} alt="2FA QR" className="w-44 h-44 rounded-xl border border-[#EAE3D2] bg-white" data-testid="twofa-qr" />
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7A8A81] mb-1">Secret (backup)</div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-[#FAF7EE] px-3 py-2 rounded-lg flex-1 break-all">{setup.secret}</code>
                <button onClick={copySecret} className="text-[#0B5D3B]" data-testid="twofa-copy">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div>
            <p className="text-sm text-[#4A554E] mb-4"><strong>Step 2.</strong> Enter the 6-digit code your app shows.</p>
            <input
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456" inputMode="numeric" maxLength={6}
              className="w-full px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-2xl tracking-[0.4em] text-center font-mono"
              data-testid="twofa-code"
            />
            <button onClick={enable} disabled={loading || code.length !== 6} className="btn-primary w-full mt-4 disabled:opacity-60" data-testid="twofa-enable">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : "Enable 2FA"}
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }} className="block w-full text-center mt-3 text-xs text-[#7A8A81] hover:text-[#111]" data-testid="twofa-cancel">Cancel</button>
          </div>
        </div>
      )}

      {status && (
        <div className="border-t border-[#EAE3D2] pt-5 mt-2">
          <p className="text-sm text-[#4A554E] mb-3">Need to remove 2FA? Confirm with your password and a current code:</p>
          <div className="grid md:grid-cols-3 gap-3">
            <input type="password" placeholder="Password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
              className="px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm" data-testid="twofa-disable-pw" />
            <input value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code" inputMode="numeric" maxLength={6}
              className="px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm font-mono" data-testid="twofa-disable-code" />
            <button onClick={disable} disabled={loading} className="px-5 py-3 rounded-full border border-red-200 text-red-700 hover:bg-red-50 text-sm font-medium" data-testid="twofa-disable">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable 2FA"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
